import { useContext, useMemo } from "react";
import IAction from "@models/IAction";
import EndpointsEnum from "@models/EndpointsEnum";
import { CatalogOverviewState } from "./useCatalogOverview";
import { SocketContext } from "@contexts/SocketContext";
import SupplierContext from "@contexts/SupplierContext";
import UserContext from "@contexts/UserContext";
import UiContext from "@contexts/UiContext";

interface UseCatalogActionsProps {
    state: CatalogOverviewState;
    setState: (updates: Partial<CatalogOverviewState>) => void;
    catalogGuid: string;
    handlers: {
        openPriceGroupsSidebar: () => void;
        openCategoriesSidebar: () => void;
        openHomeCategoriesSidebar: () => void;
        openOrderRowConfigModal: () => void;
        openInventoryRowConfigModal: () => void;
        syncElasticSearch: () => void;
        openQuickBooksMapping: () => void;
        showQbdItemSyncPriceGroupSelectionModal: () => void;
        openItemOptionsSidebar: () => void;
        openBarcodeLabelSidebar: () => void;
    };
}

export const useCatalogActions = ({ state, setState, catalogGuid, handlers }: UseCatalogActionsProps) => {
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);
    const socket = useContext(SocketContext)!;
    const uiContext = useContext(UiContext);

    const pushQBDItems = () => {
        const payload = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            catalogGuid,
        };

        socket.emit(EndpointsEnum.QBDPushItems, payload);
        setState({ qbdItemSyncActive: true });
    };

    const getXml = () => {
        const payload = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            type: "CATALOG",
            typeGuid: catalogGuid,
        };

        socket.emit(EndpointsEnum.QBDGetXML, payload);
    };

    const actions = useMemo(() => {
        const baseActions: IAction[] = [
            {
                name: "price_groups",
                leftIconClass: "fad fa-dollar-sign",
                fn: handlers.openPriceGroupsSidebar,
                isActive: false,
                id: "price_groups",
            },
            {
                name: "categories",
                leftIconClass: "fad fa-layer-group",
                fn: () => {
                    handlers.openCategoriesSidebar();
                },
                isActive: false,
                id: "categories",
            },
        ];

        if (supplierModel.has_home_categories) {
            baseActions.push({
                name: "home_categories",
                leftIconClass: "fad fa-layer-group",
                fn: handlers.openHomeCategoriesSidebar,
                isActive: false,
                id: "home_categories",
            });
        }

        baseActions.push({
            name: "item_options",
            leftIconClass: "fad fa-boxes",
            fn: handlers.openItemOptionsSidebar,
            isActive: false,
            id: "item_options",
        });

        baseActions.push({
            name: "barcode_labels",
            leftIconClass: "fad fa-barcode",
            fn: handlers.openBarcodeLabelSidebar,
            isActive: false,
            id: "barcode_labels",
        });

        baseActions.push(
            {
                name: "order_row_config",
                leftIconClass: "fad fa-columns",
                fn: handlers.openOrderRowConfigModal,
                isActive: false,
                isOpen: () => state.showOrderRowConfig || false,
                id: "order_row_config",
            },
            {
                name: "inventory_row_config",
                leftIconClass: "fad fa-columns",
                fn: handlers.openInventoryRowConfigModal,
                isActive: false,
                isOpen: () => state.showInventoryRowConfig || false,
                id: "inventory_row_config",
            }
        );

        baseActions.push({
            leftIconClass: "fad fa-sync",
            name: "sync_elasticsearch",
            isActive: () => state.syncElasticSearch || false,
            fn: handlers.syncElasticSearch,
            btnClass: () => (uiContext.showHiddenFeatures ? "btn btn-primary" : "btn btn-primary d-none"),
        });

        if (supplierModel.Supplier_Integration.has_qbd) {
            const quickbooksMappingAction: IAction = {
                name: "items_map",
                leftIconClass: "fad fa-exchange",
                fn: handlers.openQuickBooksMapping,
                isActive: false,
            };

            const quickbooksItemSync: IAction = {
                name: "items_sync",
                leftIconClass: "fad fa-link",
                fn: handlers.showQbdItemSyncPriceGroupSelectionModal,
                isActive: () => state.qbdItemSyncActive || false,
            };

            const quickbooksItemPush: IAction = {
                name: "items_push",
                leftIconClass: "fad fa-cloud-upload-alt",
                fn: pushQBDItems,
                isActive: () => state.qbdItemSyncActive || false,
            };

            const quickBooksGetXml: IAction = {
                name: "get_xml",
                leftIconClass: "fad fa-file-code",
                fn: getXml,
                isActive: false,
                btnClass: () => (uiContext.showHiddenFeatures ? "btn btn-primary" : "btn btn-primary d-none"),
            };

            const qbdGroup: IAction = {
                isDropdownGroup: true,
                name: "quickbooks_desktop",
                groupActions: [quickbooksMappingAction, quickbooksItemSync, quickbooksItemPush, quickBooksGetXml],
            };

            baseActions.push(qbdGroup);
        }

        return baseActions;
    }, [
        handlers,
        supplierModel.has_home_categories,
        supplierModel.Supplier_Integration.has_qbd,
        state.showOrderRowConfig,
        state.showInventoryRowConfig,
        state.syncElasticSearch,
        state.qbdItemSyncActive,
        uiContext.showHiddenFeatures,
        pushQBDItems,
        getXml,
    ]);

    return { actions };
};
